import type { FC } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import type { HypothesisCreativeEntry, HypothesisGroup, CreativeDnDMovePayload, } from '../../types/hypothesis';
import type { HypothesisPack } from '../../hooks';
import CreativesGridSkeleton from './CreativesGridSkeleton';
import CreativesEmptyState from './CreativesEmptyState';
import CreativesGroupedView from './CreativesGroupedView';
import CreativesFlatGrid from './CreativesFlatGrid';

interface TestCreativesContentProps {
  isInitialLoading: boolean;
  hasResults: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  groupByHypothesis: boolean;
  packsByHypothesis: Map<string, HypothesisPack[]>;
  groups: HypothesisGroup[];
  noHypothesis: HypothesisCreativeEntry[];
  sortedFlatCreatives: HypothesisCreativeEntry[];
  selectedCreatives: string[];
  columns: GridColDef[];
  dragEnabled?: boolean;
  canEditPackTag?: boolean;
  packTagsByIndex?: string[];
  mixedPackCreativeNameTagsById?: Map<string, string[]>;
  onGroupSelectionChange: (hypothesis: string, ids: string[]) => void;
  onPackSelectionChange: (packIndex: number, packCreativeIds: string[]) => void;
  onActivatePack?: (packIndex: number) => void;
  onFlatSelectionChange: (ids: string[]) => void;
  onCreativeMove?: (hypothesis: string, move: CreativeDnDMovePayload) => void;
}

const TestCreativesContent: FC<TestCreativesContentProps> = ({
  isInitialLoading,
  hasResults,
  hasActiveFilters,
  onClearFilters,
  groupByHypothesis,
  packsByHypothesis,
  groups,
  noHypothesis,
  sortedFlatCreatives,
  selectedCreatives,
  columns,
  dragEnabled = false,
  canEditPackTag = false,
  packTagsByIndex,
  mixedPackCreativeNameTagsById,
  onGroupSelectionChange,
  onPackSelectionChange,
  onActivatePack,
  onFlatSelectionChange,
  onCreativeMove,
}) => {
  if (isInitialLoading) {
    return <CreativesGridSkeleton />;
  }

  if (!hasResults) {
    return (
      <CreativesEmptyState
        hasActiveFilters={hasActiveFilters}
        onClearFilters={hasActiveFilters ? onClearFilters : undefined}
      />
    );
  }

  if (groupByHypothesis) {
    return (
      <CreativesGroupedView
        groups={groups}
        noHypothesis={noHypothesis}
        packsByHypothesis={packsByHypothesis}
        selectedCreatives={selectedCreatives}
        columns={columns}
        dragEnabled={dragEnabled}
        canEditPackTag={canEditPackTag}
        packTagsByIndex={packTagsByIndex}
        mixedPackCreativeNameTagsById={mixedPackCreativeNameTagsById}
        onSelectionChange={onGroupSelectionChange}
        onPackSelectionChange={onPackSelectionChange}
        onActivatePack={onActivatePack}
        onCreativeMove={onCreativeMove}
      />
    );
  }

  return (
    <CreativesFlatGrid
      rows={sortedFlatCreatives}
      columns={columns}
      selectedCreatives={selectedCreatives}
      onSelectionChange={onFlatSelectionChange}
    />
  );
};

export default TestCreativesContent;
